import { Group } from '../models';
import { GroupView } from '../views/GroupView';
import { createParticipantView } from './CreateParticipantView';
import { GroupStore } from '../api/GroupStore';
import { useQuery } from '@tanstack/react-query';
import { apiResponse } from '@app/api';
import { CohortUser } from '@models/CohortUser';
import { ViewCreatorStaleTime } from '@utils/queryCacheConfig';

export class GroupViewCreator {
  constructor(private readonly store: GroupStore = new GroupStore()) {}

  /**
   * Gets a group from the API via ID and creates a GroupView.
   * @param groupId The ID of the group to create the view for.
   * @returns A promise that resolves to a GroupView object.
   */
  public async CreateView(groupId: number): Promise<GroupView> {
    const group = await apiResponse.handleApiResponse(() => this.store.ReadOne(groupId));
    return {
      id: group.id,
      pledge: group.pledge,
      startDate: group.startDate,
      channel: group.channel,
      name: group.name,
      cohortUserId: group.cohortUserId,
      userCount: group.userCount,
      groupLogo: group.groupLogo,
      shortDescription: group.shortDescription,
      finalized: group.finalized,
      participants: group.cohortUsers?.map((u: CohortUser) => createParticipantView(u.user, u)),
    } as GroupView;
  }

  /**
   * Creates a GroupView from a Group model.
   * @param group The Group model to create the view from.
   * @returns A promise that resolves to a GroupView object.
   */
  public async CreateGroupViewFromGroup(group: Group): Promise<GroupView> {
    return {
      id: group.id,
      pledge: group.pledge,
      startDate: group.startDate,
      channel: group.channel,
      name: group.name,
      cohortUserId: group.cohortUserId,
      userCount: group.userCount,
      groupLogo: group.groupLogo,
      shortDescription: group.shortDescription,
      finalized: group.finalized,
      participants: group.cohortUsers?.map((u) => createParticipantView(u.user, u)),
    } as GroupView;
  }
}

export const useGroupViewCreator = (groupId: number) =>
  useQuery({
    queryKey: ['GroupViewCreator', groupId],
    queryFn: () => new GroupViewCreator().CreateView(groupId),
    staleTime: ViewCreatorStaleTime.GroupViewCreator,
  });
